import { Flex, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { EvidenceSectionName, EvidenceSectionNameMap, EvidenceTask } from 'api/brokerCases/types';
import { ApplicantOption } from 'pages/EvidencePage/helpers';
import { EvidenceItemActionListItem } from './EvidenceItemActionListItem';
import { TypedFormatMessageFn } from 'types/intl';
import { useMemo } from 'react';

export interface IEvidenceItemActionListProps {
  sectionName: EvidenceSectionName;
  tasks: EvidenceTask[];
  applicants: ApplicantOption[];
  onProgressLoadingChange: (isLoading: boolean) => void;
  /** Disambiguates rows when the same task id is reused for different individuals (forces a clean remount on tab switch). */
  listItemKeyPrefix?: string;
}

export const EvidenceItemActionList = ({
  sectionName,
  tasks,
  applicants,
  onProgressLoadingChange,
  listItemKeyPrefix,
}: IEvidenceItemActionListProps) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const showTooltip = sectionName !== EvidenceSectionNameMap.FURTHER_INFORMATION_REQUIRED;

  // Newest first: when a second-round task is created after review, uploads must use that task's id (not the first round's).
  const { tasksByCreatedAt, currentTaskIds } = useMemo(() => {
    const tasksByCreatedAt = [...tasks].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    const isIndependentRequestSection = sectionName === EvidenceSectionNameMap.FURTHER_INFORMATION_REQUIRED;
    const seenKey = new Set<string>();
    const currentTaskIds = new Set<string>();
    for (const t of tasksByCreatedAt) {
      if (isIndependentRequestSection) {
        currentTaskIds.add(t.id);
        continue;
      }
      const dedupeKey = `${t.name}|${String(t.payload.individualId ?? '')}`;
      if (seenKey.has(dedupeKey)) {
        continue;
      }
      seenKey.add(dedupeKey);
      currentTaskIds.add(t.id);
    }
    return { tasksByCreatedAt, currentTaskIds };
  }, [tasks, sectionName]);

  return (
    <Flex vertical className='w-full gap-2'>
      {tasksByCreatedAt.length > 0 ? (
        tasksByCreatedAt.map(task => (
          <EvidenceItemActionListItem
            key={listItemKeyPrefix ? `${listItemKeyPrefix}-${task.id}` : task.id}
            sectionName={sectionName}
            task={task}
            applicants={applicants}
            showTooltip={showTooltip}
            onProgressLoadingChange={onProgressLoadingChange}
            isCurrentTask={currentTaskIds.has(task.id)}
          />
        ))
      ) : (
        <Typography.Text className='text-sm font-normal mt-2 leading-5 text-gray-500'>
          {formatMessage({ id: 'evidence.taskList.empty' })}
        </Typography.Text>
      )}
    </Flex>
  );
};
